import clsx from 'clsx'
import LineLuxuryEscapesMonogramIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: inline-flex;
  flex-grow: 0;
  max-width: fit-content;
  flex-direction: row;
  align-items: center;
  gap: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  min-height: ${rem(28)};
  padding: ${rem(2)} ${rem(8)};

  &.tier-bronze {
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    &.kind-primary {
      background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    }
    &.kind-secondary {
      background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
    }
  }
  &.tier-silver {
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    &.kind-primary {
      background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    }
    &.kind-secondary {
      background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
    }
  }
  &.tier-gold {
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    &.kind-primary {
      background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    }
    &.kind-secondary {
      background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
    }
  }
  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
    &.kind-primary {
      background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    }
    &.kind-secondary {
      background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
    }
  }
`

const IconContainer = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  display: grid;
  place-items: center;
  height: ${rem(20)};
  width: ${rem(20)};
`

const StartIcon = styled(IconContainer)`
  ${setSvgIconSize(12)}
  border-radius: ${props => props.theme.borderRadius.round};

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }
  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }
  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }
  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const EndIcon = styled(IconContainer)`
  ${setSvgIconSize(16)}
`

const Content = styled(BodyText)`
  flex-grow: 1;
`

interface Props extends PropsWithChildren {
  tier: App.LoyaltyTier
  kind: 'primary' | 'secondary'
  /**
   * @default Brand logo
   * TODO rename to startIcon
   */
  icon?: ReactElement | null
  endIcon?: ReactElement | false
}

function LoyaltyTierPill({
  tier,
  kind,
  icon = <LineLuxuryEscapesMonogramIcon />,
  endIcon,
  children,
}: Props) {
  return <Container className={clsx(`tier-${tier}`, `kind-${kind}`)}>
    {!!icon && <StartIcon className={clsx(`tier-${tier}`)}>{icon}</StartIcon>}
    <Content variant="small" weight="bold">{children}</Content>
    {!!endIcon && <EndIcon>{endIcon}</EndIcon>}
  </Container>
}

export default LoyaltyTierPill
